import { Switch, Route } from "react-router-dom";
import { TierList } from "./pages/Default/TierList";
import { AppRoutes, RouterPaths } from "@ugg/shared/routes/app-routes";
import { CombatTierListContainer } from "./pages/Combat/CombatTierListContainer";
import { ObjectivesTierListContainer } from "./pages/Objectives/ObjectivesTierListContainer";
import { DuoTierListContainer } from "./pages/Duo/DuoTierListContainer";
import { ArenaTierListContainer } from "@ugg/shared/components/TierList/pages/Arena/ArenaTierListContainer";
import { ArenaDuoTierListContainer } from "@ugg/shared/components/TierList/pages/Arena/ArenaDuoTierListContainer";
import {
  AramMayhemChampionTierListContainer,
  AramMayhemAugmentTierListByRarityContainer,
} from "@ugg/shared/components/TierList/pages/AramMayhem/AramMayhemTierListContainer";
import { ClassicChampionTierListContainer } from "@ugg/shared/components/TierList/pages/Classic/ClassicTierListContainer";

// ─────────────────────────────────────────────────────────────────────
// The single tier-list route table, shared by the website and the
// desktop app.
//
// There used to be a second copy in packages/ugg/src that the website
// rendered while the app rendered this one. They drifted: the web copy
// picked up the Classic and ARAM Mayhem routes and this one never did,
// so the app fell through to the default <TierList /> on those URLs and
// rendered an empty table. (It *reached* them because
// RouterPaths.TIER_LIST includes the SPECIAL_GAME_MODE_TIER_LIST
// catch-all "/lol/:gameMode-tier-list", which matches all three.)
//
// The shell-specific bits are both props for the same reason: they're
// website components living in packages/ugg/src that can't be imported
// from here. Each shell injects (or omits) its own.
//
//   patchNotesCTA  the WP-GraphQL patch-notes button
//   bannerSlot     an optional promo/announcement banner (see below)
// ─────────────────────────────────────────────────────────────────────

interface TierListRoutesProps {
  inNewWindow?: boolean;
  patchNotesCTA?: React.ReactNode;
  // Optional banner rendered inside every tier list, between the page nav and
  // the filter bar. Forwarded to all eight containers so a single value here
  // reaches every tier-list page at once — that's the whole point of the slot,
  // and why it's plumbed even while nothing passes it.
  //
  // NOTHING PASSES IT TODAY, AND THAT IS EXPECTED. It last carried
  // `<StickyNewsBanner className="sm:hidden" />` (a mobile-only news banner,
  // still present at src/components/TierList/TierListNewsBanner.tsx). When that
  // campaign ended the producer was dropped but the plumbing was left in place;
  // it was then removed as unused, which is what this comment exists to
  // prevent a second time. Restoring a banner should be a one-line change in
  // the shell, not a re-plumb of eight files.
  bannerSlot?: React.ReactNode;
}

export const TierListRoutes = ({ inNewWindow, patchNotesCTA, bannerSlot }: TierListRoutesProps) => {
  return (
    <Switch>
      <Route
        path={AppRoutes.ARENA_TIER_LIST}
        render={() => <ArenaTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.ARENA_DUO_TIER_LIST}
        render={() => <ArenaDuoTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.DUO_TIER_LIST}
        render={() => <DuoTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.COMBAT_TIER_LIST}
        render={() => <CombatTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.OBJECTIVES_TIER_LIST}
        render={() => <ObjectivesTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.CLASSIC_TIER_LIST}
        render={() => <ClassicChampionTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.ARAM_MAYHEM_TIER_LIST}
        render={() => <AramMayhemChampionTierListContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      <Route
        path={AppRoutes.ARAM_MAYHEM_AUGMENT_TIER_LIST}
        render={() => <AramMayhemAugmentTierListByRarityContainer patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
      {/* Must stay last — RouterPaths.TIER_LIST carries the
          "/lol/:gameMode-tier-list" catch-all, which matches every
          specific route above. */}
      <Route
        path={RouterPaths.TIER_LIST}
        render={() => <TierList inNewWindow={inNewWindow} patchNotesCTA={patchNotesCTA} bannerSlot={bannerSlot} />}
      />
    </Switch>
  );
};
